/**
 * Everything in the world that isn't terrain or trees: radio towers, wrecked
 * landmarks, hiding places, and pickups.
 *
 * All of it is built procedurally from the seeded world description, so there
 * are no assets to load and the client's geometry always agrees with the
 * server's idea of where things are.
 */

import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import {
  TOWERS,
  EXTRACTION,
  type World,
  type ItemKind,
  type HidingSpot,
  type Landmark,
} from '@static-pines/shared';

const STEEL = 0x6c7278;
const STEEL_DARK = 0x4a5056;
const RUST = 0x6b4a33;
const CANVAS_COL = 0x5c5744;
const WOOD = 0x5a4632;

function mat(color: number, opts: THREE.MeshLambertMaterialParameters = {}) {
  return new THREE.MeshLambertMaterial({ color, ...opts });
}

// ---------------------------------------------------------------------------
// Radio towers
// ---------------------------------------------------------------------------

/**
 * A lattice mast with a climbable ladder and a platform at the top.
 *
 * Built as a single merged geometry per tower so a whole mast is one draw call
 * — there are only a handful of these, but each is a few hundred struts.
 */
function buildTowerGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const height = TOWERS.HEIGHT;
  const legSpread = TOWERS.BASE_RADIUS * 0.55;
  const segments = 11;

  // Four tapering legs.
  const legAngles = [0.25, 0.75, 1.25, 1.75].map((a) => a * Math.PI);
  for (const angle of legAngles) {
    const bx = Math.cos(angle) * legSpread;
    const bz = Math.sin(angle) * legSpread;
    const tx = Math.cos(angle) * legSpread * 0.28;
    const tz = Math.sin(angle) * legSpread * 0.28;

    const leg = new THREE.CylinderGeometry(0.09, 0.13, height, 4);
    leg.translate(0, height / 2, 0);

    // Lean each leg inward by shearing its top toward the axis.
    const pos = leg.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const t = pos.getY(i) / height;
      pos.setX(i, pos.getX(i) + bx + (tx - bx) * t);
      pos.setZ(i, pos.getZ(i) + bz + (tz - bz) * t);
    }
    pos.needsUpdate = true;
    parts.push(leg);
  }

  // Horizontal bracing rings and diagonal cross-braces.
  for (let s = 1; s <= segments; s++) {
    const y = (s / (segments + 1)) * height;
    const t = y / height;
    const r = legSpread * (1 - 0.72 * t);

    for (let i = 0; i < 4; i++) {
      const a0 = legAngles[i]!;
      const a1 = legAngles[(i + 1) % 4]!;
      const p0 = new THREE.Vector3(Math.cos(a0) * r, y, Math.sin(a0) * r);
      const p1 = new THREE.Vector3(Math.cos(a1) * r, y, Math.sin(a1) * r);
      parts.push(strut(p0, p1, 0.05));

      // Diagonal to the next ring up.
      if (s < segments) {
        const yUp = ((s + 1) / (segments + 1)) * height;
        const rUp = legSpread * (1 - 0.72 * (yUp / height));
        const p2 = new THREE.Vector3(Math.cos(a1) * rUp, yUp, Math.sin(a1) * rUp);
        parts.push(strut(p0, p2, 0.04));
      }
    }
  }

  // Top platform.
  const platform = new THREE.CylinderGeometry(1.9, 1.9, 0.16, 8);
  platform.translate(0, height, 0);
  parts.push(platform);

  const rail = new THREE.TorusGeometry(1.85, 0.05, 4, 10);
  rail.rotateX(Math.PI / 2);
  rail.translate(0, height + 1.0, 0);
  parts.push(rail);

  // Antenna array above the platform.
  const mast = new THREE.CylinderGeometry(0.08, 0.12, 6, 5);
  mast.translate(0, height + 3, 0);
  parts.push(mast);

  for (let i = 0; i < 3; i++) {
    const dish = new THREE.CylinderGeometry(0.9, 0.9, 0.1, 8);
    dish.rotateZ(Math.PI / 2);
    dish.translate(0.6, height + 1.6 + i * 1.5, 0);
    dish.rotateY((i * Math.PI * 2) / 3);
    parts.push(dish);
  }

  const merged = BufferGeometryUtils.mergeGeometries(
    parts.map((p) => (p.index ? p.toNonIndexed() : p)),
    false,
  );
  if (!merged) throw new Error('failed to merge tower geometry');
  merged.computeVertexNormals();
  return merged;
}

/** A box beam between two points. */
function strut(a: THREE.Vector3, b: THREE.Vector3, thickness: number): THREE.BufferGeometry {
  const length = a.distanceTo(b);
  const geo = new THREE.BoxGeometry(thickness, length, thickness);

  const mid = a.clone().add(b).multiplyScalar(0.5);
  const dir = b.clone().sub(a).normalize();
  const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);

  geo.applyQuaternion(quat);
  geo.translate(mid.x, mid.y, mid.z);
  return geo;
}

/** The ladder players actually climb, offset onto one face of the mast. */
function buildLadder(): THREE.Group {
  const group = new THREE.Group();
  const height = TOWERS.HEIGHT;
  const railMat = mat(RUST);

  for (const side of [-0.32, 0.32]) {
    const rail = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, height, 4), railMat);
    rail.position.set(side, height / 2, 0);
    group.add(rail);
  }

  const rungGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.64, 4);
  rungGeo.rotateZ(Math.PI / 2);
  const rungs = new THREE.InstancedMesh(rungGeo, railMat, Math.floor(height / 0.35));
  const m = new THREE.Matrix4();
  for (let i = 0; i < rungs.count; i++) {
    m.makeTranslation(0, 0.4 + i * 0.35, 0);
    rungs.setMatrixAt(i, m);
  }
  rungs.instanceMatrix.needsUpdate = true;
  group.add(rungs);

  return group;
}

// ---------------------------------------------------------------------------
// Landmarks
// ---------------------------------------------------------------------------

function buildLandmark(kind: Landmark['kind']): THREE.Group {
  const group = new THREE.Group();

  switch (kind) {
    case 'car': {
      const body = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.85, 4.4), mat(0x4a3a38));
      body.position.y = 0.85;
      group.add(body);

      const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.75, 2.0), mat(0x2c2a2c));
      cabin.position.set(0, 1.6, -0.2);
      group.add(cabin);

      const wheelGeo = new THREE.CylinderGeometry(0.42, 0.42, 0.3, 8);
      wheelGeo.rotateZ(Math.PI / 2);
      for (const [x, z] of [
        [-1.0, 1.4],
        [1.0, 1.4],
        [-1.0, -1.4],
        [1.0, -1.4],
      ]) {
        const wheel = new THREE.Mesh(wheelGeo, mat(0x1a1a1c));
        wheel.position.set(x!, 0.42, z!);
        group.add(wheel);
      }
      break;
    }

    case 'bus': {
      const body = new THREE.Mesh(new THREE.BoxGeometry(2.6, 2.6, 9), mat(0x4a4a30));
      body.position.y = 1.7;
      body.rotation.z = 0.08; // listing, long abandoned
      group.add(body);
      break;
    }

    case 'tent': {
      const tent = new THREE.Mesh(new THREE.ConeGeometry(1.8, 2.0, 4), mat(CANVAS_COL));
      tent.position.y = 1.0;
      tent.rotation.y = Math.PI / 4;
      group.add(tent);

      // A long-dead campfire.
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.8, 0.16, 4, 8), mat(0x50524e));
      ring.rotation.x = Math.PI / 2;
      ring.position.set(3, 0.14, 0);
      group.add(ring);
      break;
    }

    case 'shack': {
      const walls = new THREE.Mesh(new THREE.BoxGeometry(4, 2.6, 3.4), mat(WOOD));
      walls.position.y = 1.3;
      group.add(walls);

      const roof = new THREE.Mesh(new THREE.ConeGeometry(3.2, 1.2, 4), mat(0x3e3126));
      roof.position.y = 3.1;
      roof.rotation.y = Math.PI / 4;
      group.add(roof);
      break;
    }

    case 'rocks': {
      for (let i = 0; i < 5; i++) {
        const r = 0.7 + (i % 3) * 0.5;
        const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(r, 0), mat(0x5d605c));
        rock.position.set(Math.cos(i * 2.1) * 2.2, r * 0.5, Math.sin(i * 2.1) * 2.2);
        rock.rotation.set(i, i * 2, i * 3);
        group.add(rock);
      }
      break;
    }
  }

  group.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return group;
}

// ---------------------------------------------------------------------------
// Hiding spots
// ---------------------------------------------------------------------------

function buildHidingSpot(kind: HidingSpot['kind']): THREE.Object3D {
  switch (kind) {
    case 'log': {
      const log = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 0.95, 4.2, 8), mat(0x4a3b2a));
      log.rotation.z = Math.PI / 2;
      log.position.y = 0.9;
      return log;
    }
    case 'thicket': {
      const group = new THREE.Group();
      for (let i = 0; i < 4; i++) {
        const bush = new THREE.Mesh(
          new THREE.IcosahedronGeometry(1.1 + (i % 2) * 0.4, 0),
          mat(0x2c3f22),
        );
        bush.position.set(Math.cos(i * 1.7) * 0.9, 0.9, Math.sin(i * 1.7) * 0.9);
        group.add(bush);
      }
      return group;
    }
    case 'locker': {
      const locker = new THREE.Mesh(new THREE.BoxGeometry(0.9, 2.0, 0.7), mat(STEEL_DARK));
      locker.position.y = 1.0;
      return locker;
    }
    case 'trunk': {
      const trunk = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.7, 1.2), mat(0x3d3230));
      trunk.position.y = 0.35;
      return trunk;
    }
  }
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

const ITEM_COLORS: Record<ItemKind, number> = {
  battery: 0x2f7a3f,
  flare: 0xb03028,
  map: 0xb59a63,
  compass: 0xc9a227,
  radio: 0x3b4750,
  booster: 0x8a4fbf,
};

function buildItemGeometry(kind: ItemKind): THREE.BufferGeometry {
  switch (kind) {
    case 'battery':
      return new THREE.CylinderGeometry(0.07, 0.07, 0.22, 6);
    case 'flare':
      return new THREE.CylinderGeometry(0.05, 0.05, 0.3, 6);
    case 'map':
      return new THREE.BoxGeometry(0.26, 0.03, 0.2);
    case 'compass':
      return new THREE.CylinderGeometry(0.09, 0.09, 0.035, 10);
    case 'radio':
      return new THREE.BoxGeometry(0.16, 0.28, 0.09);
    case 'booster':
      return new THREE.OctahedronGeometry(0.14, 0);
  }
}

// ---------------------------------------------------------------------------
// The scene assembly
// ---------------------------------------------------------------------------

/** Everything on a tower whose appearance depends on its state. */
interface TowerVisual {
  /** Beacon lamps up the mast — dark until the tower is powered. */
  lamps: THREE.Mesh[];
  lampLight: THREE.PointLight;
}

export class Props {
  readonly group = new THREE.Group();

  /** Item id -> mesh, so pickups can be hidden when collected. */
  private readonly itemMeshes = new Map<number, THREE.Object3D>();
  private readonly itemGlow = new Map<number, THREE.Mesh>();
  private readonly towerVisuals = new Map<number, TowerVisual>();
  private glowPhase = 0;

  constructor(private readonly world: World) {
    this.group.name = 'props';

    this.buildTowers();
    this.buildLandmarks();
    this.buildHidingSpots();
    this.buildItems();
    this.buildExtractionZone();
  }

  private buildTowers(): void {
    const geometry = buildTowerGeometry();
    const material = mat(STEEL);

    for (const site of this.world.towers) {
      const mast = new THREE.Mesh(geometry, material);
      mast.position.set(site.x, site.y, site.z);
      mast.rotation.y = site.rotation;
      mast.castShadow = true;
      this.group.add(mast);

      const ladder = buildLadder();
      // Sits on the face the player climbs, matching LocalPlayer's rail lock.
      ladder.position.set(
        site.x + Math.cos(site.rotation) * TOWERS.LADDER_WIDTH,
        site.y,
        site.z + Math.sin(site.rotation) * TOWERS.LADDER_WIDTH,
      );
      ladder.rotation.y = -site.rotation;
      this.group.add(ladder);

      // A dim red aircraft-warning lamp: the one light in the forest that is
      // always on, and the only way to spot a tower from a distance.
      const lamp = new THREE.PointLight(0xff2a1a, 2.2, 26, 2);
      lamp.position.set(site.x, site.y + TOWERS.HEIGHT + 6, site.z);
      this.group.add(lamp);

      const bulb = new THREE.Mesh(
        new THREE.SphereGeometry(0.28, 6, 5),
        new THREE.MeshBasicMaterial({ color: 0xff3020 }),
      );
      bulb.position.copy(lamp.position);
      this.group.add(bulb);

      this.group.add(this.buildTowerVisual(site));
    }
  }

  /**
   * The state-driven parts of a tower: beacon lamps that come alive when a
   * transmission starts, and the ring marking the safe zone once it is done.
   *
   * An invisible safe zone would be useless, so the boundary is drawn on the
   * ground where players can actually see where safety begins.
   */
  private buildTowerVisual(site: { id: number; x: number; y: number; z: number }): THREE.Group {
    const group = new THREE.Group();

    // Lamps climbing the mast.
    const lamps: THREE.Mesh[] = [];
    const lampGeo = new THREE.SphereGeometry(0.22, 6, 5);
    for (let i = 0; i < 5; i++) {
      const lamp = new THREE.Mesh(
        lampGeo,
        new THREE.MeshBasicMaterial({ color: 0x1c2228 }),
      );
      const t = 0.18 + i * 0.19;
      lamp.position.set(site.x, site.y + TOWERS.HEIGHT * t, site.z);
      group.add(lamp);
      lamps.push(lamp);
    }

    const lampLight = new THREE.PointLight(0xffc65a, 0, 60, 2);
    lampLight.position.set(site.x, site.y + TOWERS.HEIGHT * 0.55, site.z);
    group.add(lampLight);

    // No boundary marker on the ground: the lit mast tells you which towers
    // are live, and the extent of the safety is something you learn by feel
    // rather than by reading a line drawn on the forest floor.
    this.towerVisuals.set(site.id, { lamps, lampLight });
    return group;
  }

  /**
   * Drive tower appearance from game state.
   *
   * Transmitting pulses amber — visible across the forest, which is the point:
   * it advertises exactly where a player is and what they are doing. Activated
   * goes steady green and reveals the safe zone.
   */
  syncTowers(
    towers: ReadonlyArray<{ id: number; activated: boolean; transmitting: boolean }>,
    time: number,
  ): void {
    for (const tower of towers) {
      const visual = this.towerVisuals.get(tower.id);
      if (!visual) continue;

      if (tower.activated) {
        const colour = 0x7fd8a0;
        for (const lamp of visual.lamps) {
          (lamp.material as THREE.MeshBasicMaterial).color.setHex(colour);
        }
        visual.lampLight.color.setHex(colour);
        visual.lampLight.intensity = 55;
        continue;
      }

      if (tower.transmitting) {
        // Rising pulse — it reads as something powering up.
        const pulse = 0.45 + 0.55 * Math.abs(Math.sin(time * 5.5));
        const colour = new THREE.Color(0xffc65a).multiplyScalar(pulse);
        for (const lamp of visual.lamps) {
          (lamp.material as THREE.MeshBasicMaterial).color.copy(colour);
        }
        visual.lampLight.color.setHex(0xffc65a);
        visual.lampLight.intensity = 70 * pulse;
      } else {
        for (const lamp of visual.lamps) {
          (lamp.material as THREE.MeshBasicMaterial).color.setHex(0x1c2228);
        }
        visual.lampLight.intensity = 0;
      }
    }
  }

  private buildLandmarks(): void {
    for (const landmark of this.world.landmarks) {
      const object = buildLandmark(landmark.kind);
      object.position.set(landmark.x, landmark.y, landmark.z);
      object.rotation.y = landmark.rotation;
      this.group.add(object);
    }
  }

  private buildHidingSpots(): void {
    for (const spot of this.world.hidingSpots) {
      const object = buildHidingSpot(spot.kind);
      object.position.set(spot.x, spot.y + object.position.y, spot.z);
      object.rotation.y = spot.rotation;
      object.traverse((o) => {
        if ((o as THREE.Mesh).isMesh) o.castShadow = true;
      });
      this.group.add(object);
    }
  }

  private buildItems(): void {
    // Grouped by kind so each kind is a single instanced draw... except we need
    // per-item visibility, so individual meshes are simpler and there are only
    // a few dozen.
    for (const item of this.world.items) {
      const mesh = new THREE.Mesh(
        buildItemGeometry(item.kind),
        mat(ITEM_COLORS[item.kind], { emissive: ITEM_COLORS[item.kind], emissiveIntensity: 0.35 }),
      );
      mesh.position.set(item.x, item.y + 0.22, item.z);
      mesh.castShadow = true;
      this.group.add(mesh);
      this.itemMeshes.set(item.id, mesh);

      // A faint halo so a dropped battery in deep grass is findable at all.
      const glow = new THREE.Mesh(
        new THREE.SphereGeometry(0.26, 8, 6),
        new THREE.MeshBasicMaterial({
          color: ITEM_COLORS[item.kind],
          transparent: true,
          opacity: 0.22,
          depthWrite: false,
        }),
      );
      glow.position.copy(mesh.position);
      this.group.add(glow);
      this.itemGlow.set(item.id, glow);
    }
  }

  private buildExtractionZone(): void {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(EXTRACTION.ZONE_RADIUS, 0.25, 6, 40),
      new THREE.MeshBasicMaterial({ color: 0x35d17a, transparent: true, opacity: 0.35 }),
    );
    ring.rotation.x = Math.PI / 2;
    ring.position.set(
      this.world.extraction.x,
      this.world.extraction.y + 0.3,
      this.world.extraction.z,
    );
    ring.name = 'extraction-ring';
    ring.visible = false; // only shown once rescue is inbound
    this.group.add(ring);
  }

  setExtractionVisible(visible: boolean): void {
    const ring = this.group.getObjectByName('extraction-ring');
    if (ring) ring.visible = visible;
  }

  /** Hide items the server says have been collected. */
  syncTakenItems(takenIds: Iterable<number>): void {
    for (const id of takenIds) {
      const mesh = this.itemMeshes.get(id);
      if (mesh) mesh.visible = false;
      const glow = this.itemGlow.get(id);
      if (glow) glow.visible = false;
    }
  }

  update(dt: number): void {
    // Gentle pulse so pickups catch the eye without looking like a video-game
    // objective marker.
    this.glowPhase += dt * 2.2;
    const scale = 1 + Math.sin(this.glowPhase) * 0.12;
    for (const glow of this.itemGlow.values()) {
      if (glow.visible) glow.scale.setScalar(scale);
    }
  }
}
